var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createContext, useState, useMemo } from 'react';
import { useField } from 'formik';
import * as Styled from './FormControl.styled';
export var FormControlContext = createContext(null);
/**
 * Custom hook for using form control context in a component
 */
export var useFormControl = function () {
    var context = React.useContext(FormControlContext);
    if (context === null) {
        throw new Error('useFormControl must be used within a FormControl tree');
    }
    return context;
};
/**
 * `FormControl` is used to provide form control context to an input element.
 * This ensures high flexibility and reusability across the children of `FormControl`.
 * This component is meant for implementation in a Formik form.
 */
var FormControl = function (_a) {
    var children = _a.children, _b = _a.component, Component = _b === void 0 ? Styled.Container : _b, containerClass = _a.containerClass, props = __rest(_a, ["children", "component", "containerClass"]);
    var _c = useField(props), field = _c[0], meta = _c[1];
    var _d = useState(false), isFocused = _d[0], setIsFocused = _d[1];
    var inputProps = useMemo(function () { return ({
        name: field.name,
        value: field.value,
        onChange: field.onChange,
        onFocus: function () { return setIsFocused(true); },
        onBlur: function (event) {
            field.onBlur(event);
            setIsFocused(false);
        }
    }); }, [field.name, field.value, field.onChange]);
    var formContext = {
        inputProps: inputProps,
        isFocused: isFocused,
        isEmpty: !field.value,
        errorMessage: meta.touched && meta.error,
        hasActivity: meta.touched || isFocused
    };
    var renderChildren = typeof children === 'function' ? children(formContext) : children;
    return (React.createElement(FormControlContext.Provider, { value: formContext },
        React.createElement(Component, { className: containerClass }, renderChildren)));
};
export default FormControl;
